import { useState, useMemo } from 'react';
import { turkishCities } from '../../../mocks/cities';

const POPULAR_CITIES = ['İstanbul', 'Ankara', 'İzmir', 'Bursa', 'Antalya'];

interface InlineCitySelectorProps {
  accentColor: string;
  targetName: string;
  onClose: () => void;
  onSend: (city: string) => void;
}

export default function InlineCitySelector({
  accentColor,
  targetName,
  onClose,
  onSend,
}: InlineCitySelectorProps) {
  const [citySearch, setCitySearch] = useState('');
  const [selectedCity, setSelectedCity] = useState('');

  const filtered = useMemo(
    () =>
      turkishCities.filter((c) =>
        c.toLocaleLowerCase('tr').includes(citySearch.toLocaleLowerCase('tr'))
      ),
    [citySearch]
  );

  return (
    <div
      className="mx-3 mb-4 rounded-xl overflow-hidden"
      style={{ background: 'rgba(0,0,0,0.45)', border: `1px solid ${accentColor}40` }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Panel header */}
      <div
        className="flex items-center justify-between px-3 py-2.5 border-b"
        style={{ borderColor: `${accentColor}25` }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <i className="ri-map-pin-2-fill text-sm flex-shrink-0" style={{ color: accentColor }} />
          <span className="text-white text-xs font-semibold truncate">
            {targetName} — Şehir Seç
          </span>
        </div>
        <button
          onClick={onClose}
          className="w-5 h-5 flex items-center justify-center rounded-full text-gray-500 hover:text-white hover:bg-white/10 transition-all duration-200 flex-shrink-0 ml-2 cursor-pointer whitespace-nowrap"
          aria-label="Kapat"
        >
          <i className="ri-close-line text-xs" />
        </button>
      </div>

      {/* Popular cities */}
      <div className="px-3 pt-2.5 pb-2">
        <p className="text-gray-600 text-xs mb-2 flex items-center gap-1">
          <i className="ri-fire-line text-orange-400 text-xs" />
          Popüler
        </p>
        <div className="flex flex-wrap gap-1.5">
          {POPULAR_CITIES.map((city) => (
            <button
              key={city}
              onClick={() => setSelectedCity(city)}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105 ${
                selectedCity === city ? 'text-white' : 'text-gray-300 hover:text-white'
              }`}
              style={
                selectedCity === city
                  ? {
                      background: `linear-gradient(135deg, ${accentColor}35, ${accentColor}20)`,
                      border: `1px solid ${accentColor}70`,
                      boxShadow: `0 0 10px ${accentColor}25`,
                    }
                  : {
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.12)',
                    }
              }
            >
              {selectedCity === city && (
                <i className="ri-check-line mr-0.5 text-xs" style={{ color: accentColor }} />
              )}
              {city}
            </button>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="mx-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }} />

      {/* Search input */}
      <div className="px-3 pt-2 pb-1.5">
        <div className="relative">
          <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-xs" />
          <input
            type="text"
            placeholder="Tüm şehirler..."
            value={citySearch}
            onChange={(e) => setCitySearch(e.target.value)}
            className="w-full pl-7 pr-3 py-1.5 rounded-lg text-xs text-white placeholder-gray-600 bg-white/5 border border-white/10 focus:outline-none transition-colors duration-200"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          />
          {citySearch && (
            <button
              onClick={() => setCitySearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-circle-line text-xs" />
            </button>
          )}
        </div>
      </div>

      {/* City list */}
      <div className="overflow-y-auto px-3 pb-2" style={{ maxHeight: '150px' }}>
        {filtered.length === 0 ? (
          <p className="text-gray-600 text-xs text-center py-3">Şehir bulunamadı</p>
        ) : (
          <div className="grid grid-cols-2 gap-1">
            {filtered.map((city) => (
              <button
                key={city}
                onClick={() => setSelectedCity(city)}
                className={`text-left px-2.5 py-1.5 rounded-lg text-xs transition-all duration-150 cursor-pointer whitespace-nowrap truncate ${
                  selectedCity === city
                    ? 'text-white font-semibold'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
                style={
                  selectedCity === city
                    ? {
                        background: `linear-gradient(135deg, ${accentColor}30, ${accentColor}18)`,
                        border: `1px solid ${accentColor}60`,
                      }
                    : { border: '1px solid transparent' }
                }
              >
                {selectedCity === city && (
                  <i className="ri-check-line mr-1 text-xs" style={{ color: accentColor }} />
                )}
                {city}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Send button */}
      <div className="px-3 pb-3 pt-1">
        <button
          onClick={() => selectedCity && onSend(selectedCity)}
          disabled={!selectedCity}
          className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 cursor-pointer whitespace-nowrap ${
            selectedCity
              ? 'text-white hover:scale-[1.02] active:scale-[0.98]'
              : 'text-gray-600 cursor-not-allowed'
          }`}
          style={
            selectedCity
              ? {
                  background: 'linear-gradient(135deg, #25d366, #128c7e)',
                  boxShadow: '0 4px 20px rgba(37,211,102,0.3)',
                }
              : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }
          }
        >
          <i className="ri-whatsapp-line text-base" />
          {selectedCity ? `${selectedCity} için Kampanya Al` : 'Önce Şehir Seç'}
          {selectedCity && <i className="ri-arrow-right-line text-sm" />}
        </button>
      </div>
    </div>
  );
}
